/**
 * JOAdminSidebarInjector — adds extra links to the Cockpit admin sidebar
 * (Ateliers, Clients, Chantiers) WITHOUT modifying the core AdminLayout.jsx.
 *
 * Strategy: locate the existing "Équipe" sidebar link as an anchor and
 * insert sibling nodes right after it. Idempotent — each link uses a
 * marker class so we don't insert duplicates.
 *
 * Renderless component — only runs in useEffect.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const EXTRA_LINKS = [
  // Ateliers temporarily hidden from sidebar — route still accessible via URL
  // { path: "/admin/ateliers", label: "Ateliers", marker: "jo-admin-ateliers" },
  { path: "/admin/clients", label: "Clients", marker: "jo-admin-clients" },
  { path: "/admin/chantiers", label: "Chantiers", marker: "jo-admin-chantiers" },
  { path: "/admin/facturation-pro", label: "Facturation pro", marker: "jo-admin-billing-pro" },
  { path: "/admin/analyse-clients", label: "Analyse clients", marker: "jo-admin-client-analytics" },
  { path: "/admin/destockage", label: "Déstockage", marker: "jo-admin-destockage" },
];

export default function JOAdminSidebarInjector() {
  const location = useLocation();

  useEffect(() => {
    if (!location.pathname.startsWith("/admin")) return;

    const tryInject = () => {
      // Prefer "Commandes" as anchor (under Relations Clients group which is
      // visible on this admin layout). Fall back to "Équipe".
      const anchorEl =
        document.querySelector('a[href="/admin/orders"]') ||
        document.querySelector('a[href="/admin/team"]');
      if (!anchorEl) return false;
      const parentLi = anchorEl.closest("li") || anchorEl.parentElement;
      if (!parentLi || !parentLi.parentElement) return false;

      // Insert in reverse order so the final visual order matches EXTRA_LINKS
      let anchor = parentLi;
      for (const link of EXTRA_LINKS) {
        if (document.querySelector(`.${link.marker}`)) {
          // Move anchor forward to this existing node so we keep ordering
          const existing = document.querySelector(`.${link.marker}`);
          if (existing) anchor = existing;
          continue;
        }
        const clone = parentLi.cloneNode(true);
        clone.classList.add(link.marker);
        const newLink = clone.querySelector("a");
        if (!newLink) return false;
        newLink.setAttribute("href", link.path);
        // Replace label
        const labelNode = Array.from(newLink.childNodes).find(
          (n) => n.nodeType === Node.TEXT_NODE && (n.textContent || "").trim().length > 0
        );
        if (labelNode) labelNode.textContent = link.label;
        else {
          const span = newLink.querySelector("span");
          if (span) span.textContent = link.label;
          else newLink.textContent = link.label;
        }
        newLink.classList.remove("active");
        if (window.location.pathname.startsWith(link.path)) newLink.classList.add("active");
        anchor.parentElement.insertBefore(clone, anchor.nextSibling);
        anchor = clone;
      }
      return true;
    };

    if (tryInject()) return;
    let tries = 0;
    const interval = setInterval(() => {
      tries++;
      if (tryInject() || tries > 20) clearInterval(interval);
    }, 250);
    return () => clearInterval(interval);
  }, [location.pathname]);

  return null;
}
